import { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { Loader2 } from 'lucide-react';
import { saveHangarTerms } from '@/services/deals.service.js';
import { TermAmountField } from '@/components/quote/QuoteControls.jsx';

// §9 — broker quoted-terms entry for hangar/property submissions
// (HANGAR-SUBMISSION-APP-CC-HANDOFF.md). Broker/UW workflow data, NOT client
// intake: the carrier's quoted property terms, stored at
// session.submission_workflow.hangar_terms in the exact shape the packet
// generator consumes as opts.terms. Coverage rows mirror the intake's coverage
// lines (only lines with a requested limit are shown — the generator omits the
// rest). Saving does NOT regenerate the packet; the panel nudges that next.

const LIABILITY_TIERS = [
  ['500/1000', '500 / 1000'],
  ['1000/1000', '1000 / 1000'],
  ['1000/2000', '1000 / 2000'],
  // The form's own escape (David 2026-11-03, "type in any number for all the terms"): any typed limit pair.
  ['other', 'Other'],
];

const UPDATE_TRADES = [
  ['wiring', 'Wiring'],
  ['plumbing', 'Plumbing'],
  ['roofing', 'Roofing'],
  ['heating', 'Heating'],
];

const COVERAGE_FIELDS = [
  ['coinsurance', 'Coinsurance %'],
  ['deductible', 'Deductible'],
  ['special_forms', 'Special Forms'],
  ['occupancy', 'Occupancy'],
];

function limitDisplay(limit) {
  const num = Number(String(limit ?? '').replace(/,/g, ''));
  if (!limit || Number.isNaN(num)) return limit ? String(limit) : '';
  return `$${num.toLocaleString('en-US')}`;
}

function seedState(initialTerms) {
  const t = initialTerms && typeof initialTerms === 'object' ? initialTerms : {};
  return {
    coverages: Object.fromEntries(
      Object.entries(t.coverages || {}).map(([k, v]) => [k, { ...v }])
    ),
    protection_class: t.protection_class || '',
    liability_limit: t.liability_limit || '',
    other_per_occurrence: t.other_per_occurrence ?? null,
    other_aggregate: t.other_aggregate ?? null,
    updates: { ...(t.updates || {}) },
  };
}

export default function HangarTermsSheet({ dealId, open, onOpenChange, coverageLines = [], initialTerms, intakeUpdates, onSaved }) {
  const [terms, setTerms] = useState(() => seedState(initialTerms));
  const [saving, setSaving] = useState(false);

  // Re-seed from the persisted layer every time the sheet opens so a cancel
  // (close without save) discards edits.
  useEffect(() => {
    if (open) setTerms(seedState(initialTerms));
  }, [open, initialTerms]);

  function setCoverageField(key, field, value) {
    setTerms((t) => ({
      ...t,
      coverages: { ...t.coverages, [key]: { ...(t.coverages[key] || {}), [field]: value } },
    }));
  }

  function setUpdateYear(trade, value) {
    setTerms((t) => ({ ...t, updates: { ...t.updates, [trade]: value } }));
  }

  function handleSave() {
    if (!dealId || saving) return;
    setSaving(true);
    saveHangarTerms(dealId, terms)
      .then((res) => {
        if (!res?.success) {
          toast.error(res?.error || 'Save failed');
          return;
        }
        toast.success('Quoted terms saved — regenerate the packet to apply them');
        onSaved?.(res.hangar_terms || {});
        onOpenChange(false);
      })
      .catch((err) => toast.error(err?.body?.error || err?.message || 'Save failed'))
      .finally(() => setSaving(false));
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Quoted terms</SheetTitle>
          <SheetDescription>
            The carrier&apos;s quoted property terms. These fill the blank quoted-term
            columns on the application packet when it is regenerated.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 py-4">
          {/* Per-coverage-line terms */}
          {coverageLines.length === 0 ? (
            <p className="text-xs italic text-text-dim">No coverage lines on the intake.</p>
          ) : (
            <div className="space-y-3">
              {coverageLines.map((line) => (
                <div key={line.key} className="rounded-card border border-border p-3">
                  <div className="mb-2 flex items-baseline justify-between gap-2">
                    <span className="text-sm font-medium text-text">{line.label}</span>
                    <span className="text-xs text-text-muted">Limit {limitDisplay(line.limit)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {COVERAGE_FIELDS.map(([field, label]) => (
                      <label key={field} className="block">
                        <span className="mb-1 block text-[11px] text-text-dim">{label}</span>
                        <Input
                          value={terms.coverages[line.key]?.[field] || ''}
                          onChange={(e) => setCoverageField(line.key, field, e.target.value)}
                          placeholder={field === 'coinsurance' ? 'e.g. 80%' : ''}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Protection class */}
          <div>
            <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-dim">ISO Protection Class</h3>
            <Input
              className="max-w-[120px]"
              value={terms.protection_class}
              onChange={(e) => setTerms((t) => ({ ...t, protection_class: e.target.value }))}
              placeholder="e.g. 5"
            />
          </div>

          {/* Premises liability tier */}
          <div>
            <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-dim">Premises Liability Limits ($000s)</h3>
            <div className="flex flex-wrap gap-2">
              {LIABILITY_TIERS.map(([id, label]) => (
                <Button
                  key={id}
                  type="button"
                  variant={terms.liability_limit === id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTerms((t) => (t.liability_limit === id
                    ? { ...t, liability_limit: '', other_per_occurrence: null, other_aggregate: null }
                    : { ...t, liability_limit: id, ...(id === 'other' ? {} : { other_per_occurrence: null, other_aggregate: null }) }))}
                >
                  {label}
                </Button>
              ))}
            </div>
            {/* Other: the carrier quoted a limit off the form's three tiers; the packet prints it beside "Other". */}
            {terms.liability_limit === 'other' && (
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div>
                  <span className="mb-1 block text-[11px] text-text-dim">Per occurrence ($)</span>
                  <TermAmountField ariaLabel="Premises liability per occurrence" value={terms.other_per_occurrence}
                    onChange={(v) => setTerms((t) => ({ ...t, other_per_occurrence: v }))} />
                </div>
                <div>
                  <span className="mb-1 block text-[11px] text-text-dim">Aggregate ($)</span>
                  <TermAmountField ariaLabel="Premises liability aggregate" value={terms.other_aggregate}
                    onChange={(v) => setTerms((t) => ({ ...t, other_aggregate: v }))} />
                </div>
              </div>
            )}
          </div>

          {/* Building updates */}
          <div>
            <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-dim">Updates &middot; Year of Last (if over 25 yrs)</h3>
            <div className="grid grid-cols-4 gap-2">
              {UPDATE_TRADES.map(([trade, label]) => (
                <label key={trade} className="block">
                  <span className="mb-1 block text-[11px] text-text-dim">{label}</span>
                  <Input
                    value={terms.updates[trade] || ''}
                    onChange={(e) => setUpdateYear(trade, e.target.value)}
                    placeholder={intakeUpdates?.[trade] ? String(intakeUpdates[trade]) : 'YYYY'}
                  />
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                Saving…
              </>
            ) : (
              'Save terms'
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
